import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import {
  OrganizationInterface,
  ReviewInterface,
  SimpleStatRatingInterface,
  SimpleStatDistributionInterface,
} from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { RatingSizeEnum } from '~/component/vtmn-rating/enums/RatingSize.enum';
import { ReviewStat } from '~/core/model/review-stats.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { VtmnPlayIconTypeEnum } from '../../../../component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TitleService } from '~/core/services/title/title.service';
import { StatfulReviewFiltersInterface } from '~/core/interfaces/statful-review-filters.interface';

@Component({
  selector: 'app-attendee-reviews-page',
  templateUrl: './reviews-page.component.html',
  styleUrls: ['./reviews-page.component.scss'],
})
export class AttendeeReviewsPageComponent implements OnInit {
  @Input() public hasLoadMoreButton: boolean;
  @Input() public hasOrganizer: boolean;
  @Input() public hasPagination = false;
  @Input() public reviews = [];
  @Output() public loadMore = new EventEmitter<void>();
  private readonly subscriptions = new Subscription();
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly ratingSize = RatingSizeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public activityId: number;
  public activityMedia: string;
  public activityName: string;
  public activityType: string;
  public averageAttributeRating: SimpleStatRatingInterface[];
  public averageRating: number;
  public hasApiError = false;
  public isLoading = true;
  public organisation: OrganizationInterface;
  public organizerId: string;
  public organizerReviews = [];
  public organizerReviewsStats;
  public ratingDistribution: SimpleStatDistributionInterface[];
  public recommendationRate: number;
  public reviewCount = 0;
  public reviewsParams = { range: '0-4' };

  constructor(
    public readonly activityService: ActivityService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly loaderService: LoaderService,
    public readonly localizeRouterService: LocalizeRouterService,
    public readonly organizerActivityService: OrganizerSharedActivityService,
    private readonly organizationService: OrganizationService,
    private readonly titleService: TitleService,
    private readonly translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    if (this.hasOrganizer === true) {
      // if organizer reviews
      this.activatedRoute?.parent?.params?.subscribe((organizerId) => {
        this.organizerId = organizerId.id;
      });
      this.subscriptions.add(
        this.organizationService
          .getOrganizationById(this.organizerId)
          .subscribe({
            next: (organizer) => {
              this.organisation = organizer;
              this.titleService.setPageTitle('seo.reviews.organizer.title', {
                organizerName: this.organisation?.name,
              });
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
              });
            },
            error: () => {
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
                this.hasApiError = true;
              });
            },
          })
      );
      this.subscriptions.add(
        this.organizationService
          .getOrganizationReviewsStats(this.organizerId)
          .subscribe({
            next: (stats: ReviewStat) => {
              this.organizerReviewsStats = stats;
              this.reviewCount = stats?.count;
              this.averageRating = stats?.averageRating;
              this.averageAttributeRating = stats?.averageAttributeRating;
              this.recommendationRate =
                stats?.recommendedCount / this.reviewCount;
              this.ratingDistribution = this.getRatingDistribution(
                stats?.ratingDistribution
              ).sort((a, b) => {
                return Number(b.code) - Number(a.code);
              });

              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
              });
            },
            error: () => {
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
                this.hasApiError = true;
              });
            },
          })
      );

      this.subscriptions.add(this.getOrganizationReviews(this.organizerId));
      return;
    }
    // if activity reviews
    this.activatedRoute.params.subscribe((activityId) => {
      this.activityId = Number(activityId.id);
      this.getActivity(this.activityId);
    });
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(
          getActivityDetailRouteName(this.activityType, this.activityId)
        )
        .toString(),
    };
  }

  public get hasResults(): boolean {
    return !this.isLoading && !this.hasApiError && this.reviewCount > 0;
  }

  public completeOrganizerReviews(reviews: ReviewInterface[]): void {
    this.organizerActivityService
      .getActivitiesByOrganizationId(this.organizerId)
      .subscribe({
        next: (act) => {
          const organizerActivities = act.items;

          // for each organizer review, retrieve the linked activity to get its name and image
          reviews.forEach((review: ReviewInterface) => {
            const linkedActivity = organizerActivities.find(
              (activity) => activity.sku === Number(review.subject.identifier)
            );

            const additionalInfo = {
              activityMedia: linkedActivity?.image?.url
                ? linkedActivity.image.url
                : '',
              activityName: linkedActivity?.name ? linkedActivity.name : '',
            };

            const reviewComplete = { ...review, ...additionalInfo };
            const newReviews = [];

            newReviews.push(reviewComplete);
            this.organizerReviews = this.organizerReviews?.concat(newReviews);
          });
        },
        error:
          // TO STUDY : TypeError: Cannot read properties of undefined (reading 'image')
          // https://activites.decathlon.fr/fr-FR/organisateur/35d0da8d-b769-435c-a782-55ac5e839918/decathlon-animation%20campus/activites
          () => {
            this.loaderService.loading$.subscribe(() => {
              this.isLoading = false;
              this.hasApiError = true;
            });
          },
      });
  }

  public cssStyle(item: string): string {
    return item.replace(/\s/g, '');
  }

  public getActivity(id: number): void {
    this.activityService.getActivity(id.toString()).subscribe({
      next: (activity) => {
        this.titleService.setPageTitle('seo.reviews.activity.title', {
          activityName: activity?.name,
        });

        this.activityName = activity.name;
        this.activityMedia = activity.image.url;
        this.activityType = activity.type;
        this.getReviews(this.activityId);
      },
      error: () => {
        this.loaderService.loading$.subscribe(() => {
          this.isLoading = false;
          this.hasApiError = true;
        });
      },
    });
  }

  public getOrganizationReviews(
    id: string,
    updateRange?: { [key: string]: unknown }
  ): void {
    this.reviewsParams = updateRange
      ? this.updateRange(this.reviewsParams)
      : { range: '0-4' };
    this.organizationService
      .getOrganizationReviews(id, this.reviewsParams)
      .subscribe({
        next: (reviews) => {
          this.hasPagination = this.organizerReviews.length < this.reviewCount;
          this.hasLoadMoreButton = this.hasPagination;
          this.completeOrganizerReviews(reviews);
        },
        error: () => {
          this.loaderService.loading$.subscribe(() => {
            this.isLoading = false;
            this.hasApiError = true;
          });
        },
      });
  }

  public getRatingDistribution(
    array: Array<{ code: string; value: number }>
  ): SimpleStatDistributionInterface[] {
    const ratingDistributionArray = [];
    if (typeof array !== 'number') {
      array.forEach((item) => {
        ratingDistributionArray.push({
          code: item.code,
          rate: item.value / this.reviewCount,
        });
      });
    }
    return ratingDistributionArray;
  }

  public getReviews(id: number, updateRange?: object): void {
    this.reviewsParams = updateRange
      ? this.updateRange(this.reviewsParams)
      : { range: '0-4' };
    this.activityService
      .getActivityReviews(id, this.reviewsParams)
      .subscribe((reviews) => {
        if (reviews.length === 0) {
          this.isLoading = false;
          return;
        }
        this.reviews = this.reviews?.concat(reviews);
        this.activityService.getActivityReviewsStats(id).subscribe((stats) => {
          this.reviewCount = stats.count;
          this.averageRating = stats.averageRating;
          this.averageAttributeRating = stats.averageAttributeRating;
          this.recommendationRate = stats.recommendedCount / this.reviewCount;
          this.hasPagination = this.reviews.length < this.reviewCount;
          this.hasLoadMoreButton = this.hasPagination;
          this.ratingDistribution = this.getRatingDistribution(
            stats.ratingDistribution
          ).sort((a, b) => {
            return Number(b.code) - Number(a.code);
          });
        });

        this.loaderService.loading$.subscribe(() => {
          this.isLoading = false;
        });
      });
  }
  public updateRange(actualRangeParam: StatfulReviewFiltersInterface): {
    range: string;
  } {
    const newRange = actualRangeParam.range.split('-').map((str) => {
      return Number(str);
    });
    this.reviewsParams = {
      range: (newRange[0] + 5).toString() + '-' + (newRange[1] + 5).toString(),
    };
    return this.reviewsParams;
  }

  public get reviewsTranslation(): string {
    return this.reviewCount > 1
      ? this.translateService.instant('reviews.number_of_reviews', {
          numberOfReviews: this.reviewCount || 0,
        })
      : this.translateService.instant('reviews.number_of_review', {
          numberOfReviews: this.reviewCount || 0,
        });
  }
}
